import { Project } from "utils/types"

const OPEN_EDITION_SENTINEL = 1000000
const DISPLAY_COMPLETE_PROJECT_IDS = new Set([
  "0xd9b7ec74c06c558a59afde6a16e614950730f44d-0",
  "0x0a1bbd57033f57e7b6743621b79fcb9eb2ce3676-32",
])

interface ProjectSupplyDisplayOptions {
  soldOutEdition?: boolean
}

export const toNumber = (value?: BigInt | string | number | null) => {
  if (value === undefined || value === null) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const formatNumber = (value?: BigInt | string | number | null) =>
  new Intl.NumberFormat("en-US").format(toNumber(value))

export const isEffectivelyOpenEdition = (project: Project) => {
  const maxInvocations = toNumber(project.maxInvocations)
  return maxInvocations >= OPEN_EDITION_SENTINEL
}

export const isDisplayComplete = (project: Project) => {
  const projectId = project.id || `${project.contract.id}-${project.projectId}`
  return DISPLAY_COMPLETE_PROJECT_IDS.has(projectId.toLowerCase())
}

export const getProjectStatus = (project: Project) => {
  if (isDisplayComplete(project)) return "Sold Out"
  if (project.paused) return "Paused"
  if (project.complete) return "Sold Out"
  if (toNumber(project.invocations) >= toNumber(project.maxInvocations) && !isEffectivelyOpenEdition(project)) {
    return "Sold Out"
  }
  return "Open"
}

export const formatSupply = (project: Project, options: ProjectSupplyDisplayOptions = {}) => {
  const invocationCount = toNumber(project.invocations)
  const invocations = formatNumber(invocationCount)
  if (isEffectivelyOpenEdition(project) && !options.soldOutEdition) return `${invocations} sold`

  const cappedMaxInvocations =
    options.soldOutEdition
      ? invocationCount || toNumber(project.maxInvocations)
      : toNumber(project.maxInvocations)

  return `${invocations} / ${formatNumber(cappedMaxInvocations)} sold`
}

export const supplyProgress = (project: Project, options: ProjectSupplyDisplayOptions = {}) => {
  if (isEffectivelyOpenEdition(project) && !options.soldOutEdition) return 0

  const maxInvocations =
    options.soldOutEdition
      ? toNumber(project.invocations)
      : toNumber(project.maxInvocations)

  if (!maxInvocations) return 0
  return Math.min(100, Math.round((toNumber(project.invocations) / maxInvocations) * 100))
}

export const collaboratorLabel = (artistName: string) => {
  if (!artistName || artistName === "r4v3n") return "r4v3n"
  return artistName.replace(/^r4v3n\s*x\s*/i, "with ")
}

const LIBRARY_LABELS: Record<string, string> = {
  js: "JavaScript",
  javascript: "JavaScript",
  p5js: "p5.js",
  p5: "p5.js",
  threejs: "three.js",
  three: "three.js",
  svg: "SVG",
  canvas: "Canvas",
  webgl: "WebGL",
  regl: "regl",
  paperjs: "Paper.js",
  aframe: "A-Frame",
  tonejs: "Tone.js",
  babylonjs: "Babylon.js",
  custom: "Custom",
  png: "PNG",
}

const EMPTY_VERSIONS = new Set(["", "na", "n/a", "0", "0.0.0", "none", "null", "undefined"])

/** Turn a raw `type@version` script descriptor (e.g. "js@na") into a clean label. */
export const formatLibrary = (raw?: string | null) => {
  if (!raw) return "On-chain"
  const [type, version] = raw.split("@")
  const cleanType = (type || "").trim()
  if (!cleanType) return "On-chain"
  const label = LIBRARY_LABELS[cleanType.toLowerCase()] || cleanType
  const cleanVersion = (version || "").trim()
  if (cleanVersion && !EMPTY_VERSIONS.has(cleanVersion.toLowerCase())) {
    return `${label} ${cleanVersion}`
  }
  return label
}
